import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceOperatorService, { OperatorStatement } from '../../../services/marketplaceOperatorService';
import { extractErrorMessage } from '../../../services/marketplaceService';
import { formatEuro } from '../../../pages/marketplaceVisibility';

const fmt = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('de-DE') : '–');

/** Vormonat als `YYYY-MM` (Standard für den Abrechnungslauf). */
export const previousMonth = (now = new Date()): string => {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Abrechnung (T9): Monatslauf auslösen und Provisionsrechnungen von Direct-Fallback-Sellern nach
 * Zahlungseingang als bezahlt markieren. Belege und Zahlungsfluss selbst liefert T4.
 */
const OperatorSettlementsPanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [statements, setStatements] = useState<OperatorStatement[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [month, setMonth] = useState(previousMonth());
    const [running, setRunning] = useState(false);
    const [runErrors, setRunErrors] = useState<string[]>([]);
    const [paying, setPaying] = useState<OperatorStatement | null>(null);
    const [reference, setReference] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            setStatements(await marketplaceOperatorService.getStatements());
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceOperator.common.loadError')));
        } finally {
            setLoading(false);
        }
    }, [translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const run = async () => {
        setRunning(true);
        setRunErrors([]);
        try {
            const result = await marketplaceOperatorService.runSettlement(month);
            const failed = result.results.filter((r) => r.error);
            const ok = result.results.length - failed.length;
            setRunErrors(failed.map((r) => `${translate('marketplaceOperator.common.companyFallback', { id: r.companyId })}: ${r.error}`));
            notify(
                result.results.length === 0
                    ? translate('marketplaceOperator.settlements.runEmpty', { month })
                    : translate('marketplaceOperator.settlements.runDone', { month, ok, failed: failed.length }),
                { type: failed.length ? 'warning' : 'success' },
            );
            await load();
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceOperator.common.actionError')), { type: 'error' });
        } finally {
            setRunning(false);
        }
    };

    const markPaid = async () => {
        if (!paying) return;
        setBusy(true);
        try {
            await marketplaceOperatorService.markStatementPaid(paying.id, reference.trim() || undefined);
            notify(translate('marketplaceOperator.settlements.markedPaid'), { type: 'success' });
            setPaying(null);
            setReference('');
            await load();
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceOperator.common.actionError')), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const canMarkPaid = (s: OperatorStatement) => s.settlementMode === 'commission' && s.status === 'open';
    const action = (s: OperatorStatement) =>
        canMarkPaid(s) ? (
            <Button size="small" variant="outlined" onClick={() => { setPaying(s); setReference(''); }}>
                {translate('marketplaceOperator.settlements.markPaid')}
            </Button>
        ) : null;
    const status = (s: OperatorStatement) => (
        <Chip size="small" variant="outlined" label={`${translate(`marketplaceOperator.settlements.statuses.${s.status}`)}${s.paidAt ? ` (${fmt(s.paidAt)})` : ''}`} />
    );
    const company = (id: number) => translate('marketplaceOperator.common.companyFallback', { id });

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>{translate('marketplaceOperator.settlements.title')}</Typography>
            <Typography variant="body2" sx={{ color: 'var(--oa-text-2)', mb: 2, maxWidth: 760 }}>{translate('marketplaceOperator.settlements.intro')}</Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <TextField
                    size="small"
                    type="month"
                    label={translate('marketplaceOperator.settlements.month')}
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <Button variant="contained" onClick={run} disabled={running || !/^\d{4}-\d{2}$/.test(month)}>
                    {translate('marketplaceOperator.settlements.run')}
                </Button>
            </Box>
            {runErrors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {runErrors.map((message) => <div key={message}>{message}</div>)}
                </Alert>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Skeleton variant="rectangular" height={160} />
            ) : statements.length === 0 && !error ? (
                <Alert severity="info">{translate('marketplaceOperator.settlements.empty')}</Alert>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['number', 'company', 'period', 'gross', 'refunds', 'fee', 'net', 'mode', 'status', 'actions'].map((c) => (
                                        <TableCell key={c} align={['gross', 'refunds', 'fee', 'net'].includes(c) ? 'right' : 'left'}>{translate(`marketplaceOperator.settlements.columns.${c}`)}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {statements.map((s) => (
                                    <TableRow key={s.id} data-testid={`statement-${s.id}`}>
                                        <TableCell>{s.documentNumber ?? s.number}</TableCell>
                                        <TableCell>{company(s.companyId)}</TableCell>
                                        <TableCell>{fmt(s.periodStart)} – {fmt(s.periodEnd)}</TableCell>
                                        <TableCell align="right">{formatEuro(s.grossTotal)}</TableCell>
                                        <TableCell align="right">{formatEuro(s.refundTotal)}</TableCell>
                                        <TableCell align="right">{formatEuro(s.platformFeeTotal)}</TableCell>
                                        <TableCell align="right">{formatEuro(s.settlementNet)}</TableCell>
                                        <TableCell>{translate(`marketplaceOperator.settlements.mode.${s.settlementMode}`)}</TableCell>
                                        <TableCell>{status(s)}</TableCell>
                                        <TableCell>{action(s)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5 }}>
                        {statements.map((s) => (
                            <Paper key={s.id} variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontWeight: 600 }}>{s.documentNumber ?? s.number}</Typography>
                                    {status(s)}
                                </Box>
                                <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>{company(s.companyId)} · {fmt(s.periodStart)} – {fmt(s.periodEnd)}</Typography>
                                <Typography variant="body2">{translate(`marketplaceOperator.settlements.mode.${s.settlementMode}`)}: {formatEuro(s.platformFeeTotal)}</Typography>
                                <Box sx={{ mt: 1 }}>{action(s)}</Box>
                            </Paper>
                        ))}
                    </Box>
                </>
            )}

            <Dialog open={!!paying} onClose={busy ? undefined : () => setPaying(null)} fullWidth maxWidth="sm" aria-labelledby="markpaid-title">
                <DialogTitle id="markpaid-title">
                    {translate('marketplaceOperator.settlements.markPaidTitle', { number: paying?.documentNumber ?? paying?.number ?? '' })}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>{translate('marketplaceOperator.settlements.markPaidIntro')}</Typography>
                    <TextField
                        label={translate('marketplaceOperator.settlements.reference')}
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        slotProps={{ htmlInput: { maxLength: 120 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPaying(null)} disabled={busy}>{translate('marketplaceOperator.common.cancel')}</Button>
                    <Button variant="contained" onClick={markPaid} disabled={busy}>{translate('marketplaceOperator.settlements.markPaidConfirm')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OperatorSettlementsPanel;
